import { Component, OnDestroy, OnInit } from "@angular/core";
import {
    FLIGHT_STATUS_MAP,
    FlightStatus,
    MitigationStats,
    StatsResult,
} from "@/_models";
import {
    AircraftService,
    AssessmentService,
    StatsService,
    UserService,
} from "@/_services";
import { EMPTY, from, Observable, of } from "rxjs";
import {
    debounceTime,
    filter,
    map,
    shareReplay,
    switchMap,
    take,
    tap,
} from "rxjs/operators";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { NgbModal } from "@ng-bootstrap/ng-bootstrap";
import { ToastService } from "@/_services/ToastService";
import { PdfConfirmExportComponent } from "@/components/shared/modals/pdf-confirm-export/pdf-confirm-export.component";
import { LoadingModalComponent } from "@/components/shared/loading-modal/loading-modal.component";
import { ReportExportResultDto } from "@/_models/stats-export-response";
import { expandingFabAnimations } from "@/_utility/animations";
import { UntypedFormBuilder, UntypedFormGroup } from "@angular/forms";
import { RadioOption } from "@/components/shared/radio-list/radio-list.component";
import { FilterService } from "@/_services/filter.service";
import moment from "moment";
import { getTimeFrameList } from "@/_models/filters/default-time-frame-list";
import { LOCAL_STORAGE_KEYS } from "@/_models/filters/local-storage-keys";
import { buildPastDaysDateRange } from "@/_utility/date.utils";
import { StatsSharedService } from "@/_services/stats-shared.service";

@Component({
    selector: "app-performance-overview",
    templateUrl: "./performance-overview.component.html",
    styleUrls: ["./performance-overview.component.scss"],
    animations: [expandingFabAnimations],
})
@UntilDestroy()
export class PerformanceOverviewComponent implements OnInit, OnDestroy {
    stats$: Observable<StatsResult>;
    statsList$: Observable<
        { assessmentId: string; summary: MitigationStats }[]
    >;
    statsData$: Observable<MitigationStats>;
    multipleStatsData$: Observable<MitigationStats[]>;
    isSingleStatMode = false;

    filterForm: UntypedFormGroup;
    currentFilters;

    formTypeOption: RadioOption[] = [
        { label: "Assessment", value: "assessment" },
        { label: "Hazard", value: "hazard" },
        { label: "Incident", value: "incident" },
        { label: "ASAP", value: "hazard" },
        { label: "investigation", value: "investigation" },
        { label: "inspection", value: "inspection" },
        { label: "checklist", value: "checklist" },
        { label: "generalForm", value: "generalForm" },
        { label: "All", value: "" },
    ];
    userList$: Observable<RadioOption[]>;
    aircraftList$: Observable<RadioOption[]>;
    formsList$: Observable<RadioOption[]>;
    formList$: Observable<RadioOption[]>;
    entries: string[] = [];
    timeFrameList: RadioOption[] = getTimeFrameList(["All"]);
    statusList: Omit<
        typeof FLIGHT_STATUS_MAP,
        FlightStatus.Pending | FlightStatus.New
    > = (({
        [FlightStatus.Pending]: _review,
        [FlightStatus.New]: _new,
        ...rest
    }) => {
        return rest;
    })(FLIGHT_STATUS_MAP);

    constructor(
        private userService: UserService,
        private statsService: StatsService,
        private aircraftService: AircraftService,
        private modalService: NgbModal,
        private toastService: ToastService,
        private formBuilder: UntypedFormBuilder,
        private assessmentService: AssessmentService,
        private filterService: FilterService,
        private statsSharedService: StatsSharedService,
    ) {}

    ngOnInit(): void {
        this.statsSharedService.setIsSingleStatMode(false);

        this.buildFilterForm();

        this.statsSharedService.exportRequested$
            .pipe(untilDestroyed(this))
            .subscribe(() => {
                this.exportPDF();
            });

        this.userList$ = this.userService.getUsers().pipe(
            map((users) =>
                users.map((user) => {
                    return { label: user.fullName, value: user._id };
                }),
            ),
            shareReplay(),
        );

        this.aircraftList$ = this.aircraftService.listAircraft().pipe(
            map((aircrafts) =>
                aircrafts.map((aircraft) => {
                    return { label: aircraft.designation, value: aircraft._id };
                }),
            ),
            shareReplay(),
        );

        this.formsList$ = this.assessmentService.listAssessments(false).pipe(
            map((assessments) =>
                assessments.map((assessment) => {
                    return { label: assessment.title, value: assessment._id };
                }),
            ),
            shareReplay(),
        );

        this.formList$ = this.assessmentService
            .getAssessmentsByType("assessment")
            .pipe(
                tap((assessments) => {
                    this.entries = assessments.map((a) => a._id);
                }),
                map((assessments) => [
                    { label: "All", value: "" },
                    ...assessments.map((assessment) => {
                        return {
                            label: assessment.title,
                            value: assessment._id,
                        };
                    }),
                ]),
                shareReplay(),
            );
    }

    ngOnDestroy(): void {
        this.statsSharedService.setIsSingleStatMode(false);
    }

    buildFilterForm() {
        const [defaultStart, defaultEnd] = buildPastDaysDateRange(-30);

        let initialFilters = {
            status: [],
            showArchived: false,
            responsiblePilot: "",
            timeFrame: -30,
            flightDate: [defaultStart, defaultEnd],
            aircraft: "",
            airport: "",
            assessment: "",
            formType: "",
        };

        const savedFilters = this.filterService.loadSimpleFilters(
            LOCAL_STORAGE_KEYS.INSIGHT_LIST_FILTERS,
        );

        if (savedFilters) {
            if (!savedFilters.flightDate || !savedFilters.flightDate.length) {
                savedFilters.flightDate = [defaultStart, defaultEnd];
            }

            if (!savedFilters.timeFrame || savedFilters.timeFrame === "") {
                const [start, end] = savedFilters.flightDate;
                const startDate = moment(start);
                const endDate = moment(end);
                const yesterday = moment().subtract(1, "day");
                const diffDays = endDate.diff(startDate, "days");

                if (endDate.isSame(yesterday, "day") && diffDays === 29) {
                    savedFilters.timeFrame = -30;
                } else if (endDate.isSame(yesterday, "day") && diffDays === 6) {
                    savedFilters.timeFrame = -7;
                } else if (
                    endDate.isSame(yesterday, "day") &&
                    diffDays === 364
                ) {
                    savedFilters.timeFrame = -365;
                } else {
                    savedFilters.timeFrame = "custom";
                }
            }

            initialFilters = { ...initialFilters, ...savedFilters };
        }

        this.filterForm = this.formBuilder.group({
            status: [initialFilters.status],
            showArchived: [initialFilters.showArchived],
            responsiblePilot: [initialFilters.responsiblePilot],
            // todo: doesn't seem to be used
            timeFrame: [initialFilters.timeFrame],
            flightDate: [initialFilters.flightDate],
            aircraft: [initialFilters.aircraft],
            airport: [initialFilters.airport],
            assessment: [initialFilters.assessment],
            // todo: doesn't seem to be used, redundant with 'assessment'
            formType: [initialFilters.formType],
        });

        this.filterForm.valueChanges
            .pipe(
                debounceTime(500),
                tap((changes) => {
                    this.currentFilters = { ...changes };
                    this.filterService.saveSimpleFilters(
                        LOCAL_STORAGE_KEYS.INSIGHT_LIST_FILTERS,
                        changes,
                    );
                }),
                tap(() => {
                    this.updateFlightList();
                }),
                untilDestroyed(this),
            )
            .subscribe();

        this.filterForm.updateValueAndValidity({ emitEvent: true });
    }

    updateFlightList(previousPeriod: string = null) {
        this.stats$ = this.statsService
            .getStats(this.filterForm.value)
            .pipe(shareReplay());

        this.statsData$ = this.stats$.pipe(
            map((stats) => {
                const result = Array.isArray(stats.data) ? null : stats.data;
                this.isSingleStatMode = !!result;
                this.statsSharedService.setIsSingleStatMode(
                    this.isSingleStatMode,
                );
                return result;
            }),
            shareReplay(),
        );

        this.multipleStatsData$ = this.stats$.pipe(
            map((stats) => {
                const result = Array.isArray(stats.data) ? stats.data : null;
                return result;
            }),
            shareReplay(),
        );
    }

    exportPDF() {
        const confirmationModal = this.modalService.open(
            PdfConfirmExportComponent,
        );
        const component =
            confirmationModal.componentInstance as PdfConfirmExportComponent;
        component.currentFilters = this.currentFilters;
        component.userList$ = this.userList$;
        component.aircraftList$ = this.aircraftList$;
        component.formList$ = this.formList$;

        from(confirmationModal.result)
            .pipe(
                take(1),
                filter((result) => !!result),
                map((result) => result as "pdf" | "csv" | "html"),
                switchMap((format) => {
                    const loading = this.modalService.open(
                        LoadingModalComponent,
                    );
                    const loadingComponent =
                        loading.componentInstance as LoadingModalComponent;
                    loadingComponent.message = "Generating report";

                    const result$ = this.statsService.exportStats(
                        this.filterForm.value,
                        format,
                    );
                    if (!result$) {
                        loading.close();
                        confirmationModal.close();
                        return EMPTY;
                    }

                    return (result$ as Observable<ReportExportResultDto>).pipe(
                        map((response) => {
                            const blobUrl = window.URL.createObjectURL(
                                response.data,
                            );
                            const link = document.createElement("a");
                            link.href = blobUrl;
                            link.download = response.filename;
                            link.setAttribute("target", "_blank");
                            return link;
                        }),
                        tap({
                            next: () => loading.close(),
                            error: () => loading.close(),
                        }),
                    );
                }),
                untilDestroyed(this),
            )
            .subscribe({
                next: (link) => {
                    if (link) {
                        link.click();
                        link.remove();
                    }
                    confirmationModal.close();
                },
                error: (err) => {
                    confirmationModal.close();
                    this.toastService.error(err, "Unable to generate report");
                },
            });
    }

    getAssessmentTitle(id: string): string {
        let match: RadioOption | undefined;
        this.formsList$.pipe(take(1)).subscribe((forms) => {
            match = forms.find((a) => a.value === id);
        });
        return match?.label || "";
    }

    protected of(data: any): Observable<any> {
        return of(data);
    }

    filterByAssessmentId(assessmentId: string) {
        this.filterForm.patchValue({
            formType: assessmentId,
        });
    }
}
